import React from 'react';
import { Header, Message, Input, Button, Icon, Card, Pagination } from 'semantic-ui-react';
import { LoadingWrapper } from '../common';
import { useAuth, useCanViewVideoLibrary } from '../auth';
import './VideoLibrary.css';
import { useVideoLibrary } from './useVideoLibrary';
import { VideoCard } from './VideoCard';
import { BlobVideoPlayerModal } from '../common';
import { VideoUploadModal } from './VideoUploadModal';
import { VideoEditModal } from './VideoEditModal';

export function VideoLibrary() {
    const auth = useAuth();
    const canManage = auth.userIsIccSupportTeam && !auth.isReadOnly;
    const canView = useCanViewVideoLibrary();

    const {
        videos,
        loading,
        error,
        searchTerm,
        page,
        totalPages,
        playerVideo,
        editingVideo,
        uploadModalOpen,
        uploading,
        uploadPhase,
        uploadProgress,
        uploadError,
        uploadSuccess,
        actionError,
        handleSearch,
        handlePageChange,
        handleOpenPlayer,
        handleClosePlayer,
        handleOpenEdit,
        handleCloseEdit,
        handleUpload,
        handleUpdate,
        handleDelete,
        handleOpenUploadModal,
        handleCloseUploadModal,
        fetchVideos
    } = useVideoLibrary();

    if (!canView) {
        return (
            <div className="video-library">
                <Header as="h2">Video Content</Header>
                <Message warning>
                    <p>You do not have permission to view this page.</p>
                </Message>
            </div>
        );
    }

    return (
        <div className="video-library">
            <Header as="h2">Video Content</Header>

            {error && (
                <Message negative>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {actionError && (
                <Message negative onDismiss={() => {}}>
                    <Message.Header>Error</Message.Header>
                    <p>{actionError}</p>
                </Message>
            )}

            {uploadSuccess && (
                <Message positive>
                    <Message.Header>Upload Successful</Message.Header>
                    <p>{uploadSuccess}</p>
                </Message>
            )}

            <div className="video-toolbar">
                <Input
                    icon="search"
                    placeholder="Search videos..."
                    value={searchTerm}
                    onChange={handleSearch}
                    aria-label="Search videos"
                />
                <Button
                    basic
                    icon
                    labelPosition="left"
                    onClick={fetchVideos}
                    title="Refresh video list"
                >
                    <Icon name="refresh" />
                    Refresh
                </Button>
                {canManage && (
                    <Button
                        primary
                        icon
                        labelPosition="left"
                        onClick={handleOpenUploadModal}
                    >
                        <Icon name="upload" />
                        Upload Video
                    </Button>
                )}
            </div>

            <LoadingWrapper loading={loading} text="Loading videos">
                {videos.length === 0 ? (
                    <Message info>No videos found.</Message>
                ) : (
                    <Card.Group itemsPerRow={4} stackable className="video-grid">
                        {videos.map((video) => (
                            <VideoCard
                                key={video.id}
                                video={video}
                                canManage={canManage}
                                onPlay={handleOpenPlayer}
                                onEdit={handleOpenEdit}
                                onDelete={handleDelete}
                            />
                        ))}
                    </Card.Group>
                )}
                {totalPages > 1 && (
                    <div className="video-pagination">
                        <Pagination
                            activePage={page}
                            totalPages={totalPages}
                            onPageChange={(_, { activePage }) => handlePageChange(activePage)}
                            ellipsisItem={null}
                        />
                    </div>
                )}
            </LoadingWrapper>

            {playerVideo != null && (
                <BlobVideoPlayerModal
                    blobName={playerVideo.blobName}
                    title={playerVideo.title}
                    onClose={handleClosePlayer}
                />
            )}

            {uploadModalOpen && (
                <VideoUploadModal
                    open={uploadModalOpen}
                    onClose={handleCloseUploadModal}
                    onUpload={handleUpload}
                    uploading={uploading}
                    uploadPhase={uploadPhase}
                    uploadProgress={uploadProgress}
                    uploadError={uploadError}
                />
            )}

            {editingVideo && (
                <VideoEditModal
                    video={editingVideo}
                    onClose={handleCloseEdit}
                    onUpdate={handleUpdate}
                    actionError={actionError}
                />
            )}
        </div>
    );
}
